import { useState, useRef, useEffect } from "react";
import { HexColorPicker } from "react-colorful";

/**
 * ColorPicker
 * - Renders a small swatch + hex text input.
 * - Clicking the swatch opens a floating HEX picker (react-colorful).
 * - Click outside or Escape closes it.
 * - onChange fires with a plain hex string ("#rrggbb") on every drag.
 */
export const ColorPicker = ({ value, onChange, ariaLabel, testId }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const safe = value && /^#[0-9a-fA-F]{3,8}$/.test(value) ? value : (value || "#ffffff");

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative inline-flex items-center gap-2">
      <button
        type="button"
        aria-label={ariaLabel || "Pick color"}
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className="w-10 h-10 rounded-md border-2 border-border shadow-inner ring-1 ring-black/20 hover:scale-105 transition-transform"
        style={{ background: safe }}
      />
      <input
        type="text"
        value={safe}
        onChange={(e) => onChange(e.target.value)}
        className="w-24 h-9 text-xs font-mono px-2 rounded border border-border bg-background text-foreground uppercase"
        data-testid={testId ? `${testId}-hex` : undefined}
      />
      {open && (
        <div
          className="absolute z-50 top-11 left-0 p-3 rounded-xl border border-border bg-popover shadow-2xl"
          style={{ minWidth: 220 }}
          data-testid={testId ? `${testId}-popover` : undefined}
        >
          <HexColorPicker color={safe} onChange={onChange} />
          <div className="mt-2 grid grid-cols-8 gap-1">
            {["#ffffff","#000000","#ef4444","#f59e0b","#eab308","#22c55e","#06b6d4","#3b82f6","#8b5cf6","#a855f7","#ec4899","#f472b6","#94a3b8","#64748b","#0f172a","#7c3aed"].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onChange(c)}
                className="w-5 h-5 rounded border border-black/20 hover:scale-110 transition-transform"
                style={{ background: c }}
                aria-label={`Preset ${c}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
